/**
 * Global Badge Utilities
 * Centralized badge color and styling configurations for consistent UI across the application
 */

// Project Status Badge Mappings
export const PROJECT_STATUS_BADGES = {
  active: {
    variant: 'success',
    class: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
  },
  completed: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  archived: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  paused: {
    variant: 'warning',
    class: 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200'
  }
}

// Feature Status Badge Mappings
export const FEATURE_STATUS_BADGES = {
  planning: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  in_progress: {
    variant: 'blue',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  },
  in_review: {
    variant: 'yellow',
    class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  },
  completed: {
    variant: 'green',
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  },
  closed: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

// Bug Status Badge Mappings
export const BUG_STATUS_BADGES = {
  open: {
    variant: 'red',
    class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  },
  in_progress: {
    variant: 'blue',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  },
  resolved: {
    variant: 'green',
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  },
  closed: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  reopened: {
    variant: 'orange',
    class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  }
}

// Test Case Status Badge Mappings
export const TEST_CASE_STATUS_BADGES = {
  draft: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  ready: {
    variant: 'blue',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  },
  active: {
    variant: 'green',
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  },
  deprecated: {
    variant: 'red',
    class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  }
}

// Test Run Status Badge Mappings
export const TEST_RUN_STATUS_BADGES = {
  not_started: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  in_progress: {
    variant: 'blue',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  },
  passed: {
    variant: 'green',
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  },
  failed: {
    variant: 'red',
    class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  },
  blocked: {
    variant: 'orange',
    class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  },
  skipped: {
    variant: 'yellow',
    class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  }
}

// Priority Badge Mappings
export const PRIORITY_BADGES = {
  low: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  medium: {
    variant: 'blue',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  },
  high: {
    variant: 'orange',
    class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  },
  critical: {
    variant: 'red',
    class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  }
}

// Severity Badge Mappings (for bugs)
export const SEVERITY_BADGES = {
  low: {
    variant: 'gray',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  },
  medium: {
    variant: 'blue',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  },
  high: {
    variant: 'orange',
    class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  },
  critical: {
    variant: 'red',
    class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  }
}

// Search Result Type Badge Mappings
export const SEARCH_TYPE_BADGES = {
  team: {
    variant: 'orange',
    class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  },
  project: {
    variant: 'indigo',
    class: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
  },
  feature: {
    variant: 'cyan',
    class: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
  },
  testcase: {
    variant: 'purple',
    class: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
  },
  bug: {
    variant: 'red',
    class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  },
  testrun: {
    variant: 'yellow',
    class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  }
}

/**
 * Get badge configuration for project status
 * @param {string} status - The project status
 * @returns {Object} Badge configuration with variant and class
 */
export const getProjectStatusBadge = (status) => {
  const normalizedStatus = status?.toLowerCase() || 'active'
  return PROJECT_STATUS_BADGES[normalizedStatus] || PROJECT_STATUS_BADGES.active
}

/**
 * Get badge configuration for feature status
 * @param {string} status - The feature status
 * @returns {Object} Badge configuration with variant and class
 */
export const getFeatureStatusBadge = (status) => {
  const normalizedStatus = status?.toLowerCase() || 'planning'
  return FEATURE_STATUS_BADGES[normalizedStatus] || FEATURE_STATUS_BADGES.planning
}

/**
 * Get badge configuration for bug status
 * @param {string} status - The bug status
 * @returns {Object} Badge configuration with variant and class
 */
export const getBugStatusBadge = (status) => {
  const normalizedStatus = status?.toLowerCase() || 'open'
  return BUG_STATUS_BADGES[normalizedStatus] || BUG_STATUS_BADGES.open
}

/**
 * Get badge configuration for test case status
 * @param {string} status - The test case status
 * @returns {Object} Badge configuration with variant and class
 */
export const getTestCaseStatusBadge = (status) => {
  const normalizedStatus = status?.toLowerCase() || 'draft'
  return TEST_CASE_STATUS_BADGES[normalizedStatus] || TEST_CASE_STATUS_BADGES.draft
}

/**
 * Get badge configuration for test run status
 * @param {string} status - The test run status
 * @returns {Object} Badge configuration with variant and class
 */
export const getTestRunStatusBadge = (status) => {
  const normalizedStatus = status?.toLowerCase() || 'not_started'
  return TEST_RUN_STATUS_BADGES[normalizedStatus] || TEST_RUN_STATUS_BADGES.not_started
}

/**
 * Get badge configuration for priority
 * @param {string} priority - The priority level
 * @returns {Object} Badge configuration with variant and class
 */
export const getPriorityBadge = (priority) => {
  const normalizedPriority = priority?.toLowerCase() || 'low'
  return PRIORITY_BADGES[normalizedPriority] || PRIORITY_BADGES.low
}

/**
 * Get badge configuration for severity
 * @param {string} severity - The severity level
 * @returns {Object} Badge configuration with variant and class
 */
export const getSeverityBadge = (severity) => {
  const normalizedSeverity = severity?.toLowerCase() || 'low'
  return SEVERITY_BADGES[normalizedSeverity] || SEVERITY_BADGES.low
}

/**
 * Get badge configuration for search result type
 * @param {string} type - The search result type
 * @returns {Object} Badge configuration with variant and class
 */
export const getSearchTypeBadge = (type) => {
  const normalizedType = type?.toLowerCase() || 'project'
  return SEARCH_TYPE_BADGES[normalizedType] || SEARCH_TYPE_BADGES.project
}

/**
 * Format status text to human-readable format
 * @param {string} status - The status to format
 * @returns {string} Formatted status
 */
export const formatStatus = (status) => {
  if (!status) return 'Unknown'
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format priority text to human-readable format
 * @param {string} priority - The priority to format
 * @returns {string} Formatted priority
 */
export const formatPriority = (priority) => {
  if (!priority) return 'Low'
  return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase()
}

/**
 * Format severity text to human-readable format
 * @param {string} severity - The severity to format
 * @returns {string} Formatted severity
 */
export const formatSeverity = (severity) => {
  if (!severity) return 'Low'
  return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase()
}

// HLTag Color Mappings
// Maps badge variants to HLTag color variants
const BADGE_TO_TAG_COLOR_MAP = {
  'gray': 'gray',
  'primary': 'primary',
  'success': 'success',
  'green': 'success',
  'error': 'error',
  'red': 'error',
  'warning': 'warning',
  'yellow': 'warning',
  'blue': 'blue',
  'blue-light': 'blue-light',
  'indigo': 'indigo',
  'purple': 'purple',
  'pink': 'pink',
  'rose': 'rose',
  'orange': 'orange-dark',
  'orange-dark': 'orange-dark',
  'cyan': 'blue-light'
}

/**
 * Convert badge variant to HLTag color
 * @param {string} variant - The badge variant
 * @returns {string} HLTag color variant
 */
export const getHLTagColor = (variant) => {
  return BADGE_TO_TAG_COLOR_MAP[variant] || 'gray'
}

/**
 * Get HLTag configuration for project status
 * @param {string} status - The project status
 * @returns {Object} HLTag configuration with color
 */
export const getProjectStatusHLTag = (status) => {
  const badgeConfig = getProjectStatusBadge(status)
  return {
    color: getHLTagColor(badgeConfig.variant)
  }
}

/**
 * Get HLTag configuration for feature status
 * @param {string} status - The feature status
 * @returns {Object} HLTag configuration with color
 */
export const getFeatureStatusHLTag = (status) => {
  const badgeConfig = getFeatureStatusBadge(status)
  return {
    color: getHLTagColor(badgeConfig.variant)
  }
}

/**
 * Get HLTag configuration for bug status
 * @param {string} status - The bug status
 * @returns {Object} HLTag configuration with color
 */
export const getBugStatusHLTag = (status) => {
  const badgeConfig = getBugStatusBadge(status)
  return {
    color: getHLTagColor(badgeConfig.variant)
  }
}

/**
 * Get HLTag configuration for test case status
 * @param {string} status - The test case status
 * @returns {Object} HLTag configuration with color
 */
export const getTestCaseStatusHLTag = (status) => {
  const badgeConfig = getTestCaseStatusBadge(status)
  return {
    color: getHLTagColor(badgeConfig.variant)
  }
}

/**
 * Get HLTag configuration for test run status
 * @param {string} status - The test run status
 * @returns {Object} HLTag configuration with color
 */
export const getTestRunStatusHLTag = (status) => {
  const badgeConfig = getTestRunStatusBadge(status)
  return {
    color: getHLTagColor(badgeConfig.variant)
  }
}

/**
 * Get HLTag configuration for search result type
 * @param {string} type - The search result type
 * @returns {Object} HLTag configuration with color
 */
export const getSearchTypeHLTag = (type) => {
  const badgeConfig = getSearchTypeBadge(type)
  return {
    color: getHLTagColor(badgeConfig.variant)
  }
}

